import CheckCircleFilledIcon from "@redotech/redo-icons/check-circle_filled.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useState } from "react";

const ALWAYS_LAST_CARRIER = "...and several more";

const LOADING_CARRIERS = [
  "XPO Logistics",
  "Estes",
  "FedEx Freight",
  "SAIA",
  "Old Dominion",
  ALWAYS_LAST_CARRIER,
];

const CARRIER_TICK_MS = 800;

interface QuotesLoadingProps {
  pending: boolean;
  onAnimationComplete(): void;
}

export const QuotesLoading = memo(function QuotesLoading({
  pending,
  onAnimationComplete,
}: QuotesLoadingProps) {
  const [completedCarriers, setCompletedCarriers] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (!pending) {
      setCompletedCarriers(new Set(LOADING_CARRIERS));
      const timeout = setTimeout(onAnimationComplete, 300);
      return () => clearTimeout(timeout);
    }

    setCompletedCarriers(new Set());
    const interval = setInterval(() => {
      setCompletedCarriers((prev) => {
        const remaining = LOADING_CARRIERS.filter(
          (c) => !prev.has(c) && c !== ALWAYS_LAST_CARRIER,
        );
        if (remaining.length === 0) {
          return prev;
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const next = remaining[Math.floor(Math.random() * remaining.length)]!;
        return new Set([...prev, next]);
      });
    }, CARRIER_TICK_MS);

    return () => clearInterval(interval);
  }, [pending, onAnimationComplete]);

  return (
    <Flex align="center" flex={1} justify="center">
      <Flex dir="column" gap="xl" style={{ width: 400 }}>
        <Flex align="center" dir="column" gap="xl">
          <RedoSpinner size="md" style="line" />
          <Text fontSize="lg" fontWeight="semibold" textColor="primary">
            Gathering carrier quotes...
          </Text>
        </Flex>
        <Flex dir="column" gap="md" pt="md">
          {LOADING_CARRIERS.map((carrier) => {
            const done = completedCarriers.has(carrier);
            return (
              <Flex align="center" gap="md" key={carrier}>
                {done ? (
                  <RedoIcon
                    color="success"
                    Icon={CheckCircleFilledIcon}
                    size={16}
                  />
                ) : (
                  <RedoSpinner size="xs" style="line" />
                )}
                <Text
                  fontSize="lg"
                  fontWeight={done ? "medium" : "regular"}
                  textColor={done ? "primary" : "secondary"}
                >
                  {carrier}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      </Flex>
    </Flex>
  );
});
